import { inject, singleton } from "tsyringe";
import { WindowRepository } from "./trackingContext/window-repository";
import { CookieRepository } from "./trackingContext/cookie-repository";
import { BeaconGenerator } from "../BeaconGenerator";
import { PageViewNumberGenerator } from "../PageViewNumberGenerator";
import { WikiConfig, TopLevelDomainExtractor } from "@identity/utils";
import { UUID, WINDOW } from "@identity/symbols";
import {
  CommunicationService,
  eventsRepository,
} from "../CommunicationService";

const MS_30_MIN = 1000 * 60 * 30;
const MS_180_DAYS = 1000 * 60 * 60 * 24 * 180;

@singleton()
export class TrackingContextSetupService {
  constructor(
    @inject(WindowRepository) private windowRepository: WindowRepository,
    @inject(CookieRepository) private cookieRepository: CookieRepository,
    @inject(UUID) private uuid: () => string,
    @inject(BeaconGenerator) private beaconGenerator: BeaconGenerator,
    @inject(PageViewNumberGenerator)
    private pageViewNumberGenerator: PageViewNumberGenerator,
    @inject(WINDOW)
    private readonly window: Window,
    @inject(WikiConfig)
    private readonly wikiConfig: WikiConfig,
    @inject(CommunicationService)
    private readonly communicationService: CommunicationService,
  ) {
    this.communicationService.on(eventsRepository.PAGE_CHANGED, () => {
      this.updatePageViewData();
    });
  }

  private set(name: string, trackingProperty: TrackingProperty) {
    this.windowRepository.set(name, trackingProperty);
    this.cookieRepository.set(name, trackingProperty);
  }

  private remove(name: string) {
    this.windowRepository.remove(name);
    this.cookieRepository.remove(name);
  }

  private updatePageViewData() {
    const domain = this.window.location.hostname;
    const topLevelDomain = TopLevelDomainExtractor.extract(domain);
    this.windowRepository.set("pvUID", {
      domain: domain,
      expiresMS: MS_30_MIN,
      value:
        this.wikiConfig.get("pvUID") || // UCP
        this.window.pvUID || // F2
        this.uuid(),
    });

    this.set("pvNumber", {
      domain: domain,
      expiresMS: MS_30_MIN,
      value: this.pageViewNumberGenerator.getNextNumberFromCookie("pvNumber"),
    });

    this.set("pvNumberGlobal", {
      domain: topLevelDomain,
      expiresMS: MS_30_MIN,
      value:
        this.pageViewNumberGenerator.getNextNumberFromCookie("pvNumberGlobal"),
    });
  }

  private updateUserTrackingData() {
    const domain = this.window.location.hostname;
    const topLevelDomain = TopLevelDomainExtractor.extract(domain);
    const beaconId =
      this.cookieRepository.get("wikia_beacon_id") ||
      this.cookieRepository.get("beaconId");
    const beaconParams = {
      domain: topLevelDomain,
      expiresMS: MS_180_DAYS,
      value: beaconId || this.beaconGenerator.generate(),
    };
    this.windowRepository.set("beaconId", beaconParams);
    this.cookieRepository.set("wikia_beacon_id", beaconParams);

    const mwSessionId = this.wikiConfig.get("sessionId", this.window.sessionId);
    const sessionId = mwSessionId || this.cookieRepository.get("sessionId");
    const sessionParams = {
      domain: topLevelDomain,
      expiresMS: MS_30_MIN,
      value: sessionId || this.uuid(),
    };
    this.set("sessionId", sessionParams);
  }

  public setup(): void {
    this.updateUserTrackingData();
    this.updatePageViewData();
  }

  public clear(): void {
    this.remove("beaconId");
    this.remove("sessionId");
    this.remove("pvNumber");
    this.remove("pvNumberGlobal");
  }
}
